// Session shading: the trading day's chapters, painted faintly BEHIND the
// candles. For XAUUSD the session is half the story -- the overlap is the
// widest stretch of the day by a clear margin (measured, not folklore) --
// and shading it turns "when did this move happen" into something the eye
// answers without reading the axis.
//
// Truth rule: the band boundaries are the SAME UTC windows the server's
// sessions.py trades by (asia 00-07, london 07-12, overlap 12-16, ny 16-21).
// This file only paints them; it decides nothing.

const BANDS = [
  { from: 0,  to: 7,  color: "rgba(216, 203, 179, 0.025)", label: "ASIA" },
  { from: 7,  to: 12, color: "rgba(201, 162, 39, 0.03)",  label: "LDN" },
  { from: 12, to: 16, color: "rgba(201, 162, 39, 0.055)",   label: "LDN+NY" },
  { from: 16, to: 21, color: "rgba(46, 158, 107, 0.028)",   label: "NY" },
];

const DAY = 86400;

class SessionBandsRenderer {
  constructor(source) {
    this._source = source;
  }

  draw(target) {
    const source = this._source;
    if (!source._enabled || !source._chart) return;
    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hRatio = scope.horizontalPixelRatio;
      const height = scope.bitmapSize.height;
      const timeScale = source._chart.timeScale();
      const range = timeScale.getVisibleRange();
      if (!range) return;

      // Walk whole UTC days across the visible span, one band at a time.
      const firstDay = Math.floor(range.from / DAY) * DAY;

      // Density discipline: zoomed out far enough that a whole day spans
      // under ~28px, the bands become a barcode -- worse than nothing.
      // Detail must FOLLOW zoom, never fight it.
      try {
        const x0 = timeScale.timeToCoordinate(Math.max(firstDay, range.from));
        const x1 = timeScale.timeToCoordinate(
          Math.min(Math.max(firstDay, range.from) + DAY, range.to));
        if (x0 !== null && x1 !== null && (x1 - x0) < 28) return;
      } catch (e) { /* paint anyway */ }

      ctx.save();
      for (let day = firstDay; day < range.to; day += DAY) {
        for (const band of BANDS) {
          const start = day + band.from * 3600;
          const end = day + band.to * 3600;
          if (end < range.from || start > range.to) continue;
          let x1 = null, x2 = null;
          try {
            x1 = timeScale.timeToCoordinate(Math.max(start, range.from));
            x2 = timeScale.timeToCoordinate(Math.min(end, range.to));
          } catch (e) { continue; }
          if (x1 === null || x2 === null || x2 <= x1) continue;
          const left = x1 * hRatio;
          const width = (x2 - x1) * hRatio;
          ctx.fillStyle = band.color;
          ctx.fillRect(left, 0, width, height);
          // The chapter's name, only when the band is wide enough to carry
          // it without becoming noise.
          if (width > 60 * hRatio) {
            ctx.fillStyle = "rgba(128, 128, 128, 0.35)";
            ctx.font = `${Math.round(9 * scope.verticalPixelRatio)}px "Inter Tight", system-ui, sans-serif`;
            ctx.textBaseline = "top";
            ctx.fillText(band.label, left + 4 * hRatio, 4 * scope.verticalPixelRatio);
          }
        }
      }
      ctx.restore();
    });
  }
}

class SessionBandsPaneView {
  constructor(source) {
    this._source = source;
    this._renderer = new SessionBandsRenderer(source);
  }
  zOrder() { return "bottom"; }
  update() {}
  renderer() { return this._renderer; }
}

export class SessionBands {
  constructor() {
    this._enabled = true;
    this._chart = null;
    this._requestUpdate = null;
    this._paneViews = [new SessionBandsPaneView(this)];
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
  }

  paneViews() { return this._paneViews; }
  updateAllViews() { this._paneViews.forEach((v) => v.update()); }
  autoscaleInfo() { return null; }

  setEnabled(on) {
    this._enabled = !!on;
    if (this._requestUpdate) this._requestUpdate();
  }
}
